import type { HTMLAttributes, ReactNode } from 'react';

import { ClassNames } from '@/shared/classNames';

type BadgeTone = 'slate' | 'green' | 'red' | 'amber' | 'blue' | 'cyan' | 'violet';

type BadgeProperties = HTMLAttributes<HTMLSpanElement> & {
  children: ReactNode;
  dot?: boolean;
  tone?: BadgeTone;
};

const toneClasses: Record<BadgeTone, string> = {
  amber: 'border-warning/20 bg-warning-wash text-warning',
  blue: 'border-info/20 bg-info-wash text-info',
  cyan: 'border-accent/20 bg-selection text-accent',
  green: 'border-healthy/20 bg-healthy-wash text-healthy',
  red: 'border-critical/20 bg-critical-wash text-critical',
  slate: 'border-line bg-sidebar text-muted',
  violet: 'border-deep/20 bg-deep/8 text-deep',
};

const dotClasses: Record<BadgeTone, string> = {
  amber: 'bg-warning',
  blue: 'bg-info',
  cyan: 'bg-accent',
  green: 'bg-healthy',
  red: 'bg-critical',
  slate: 'bg-muted',
  violet: 'bg-deep',
};

export const Badge = ({ children, className, dot = true, tone = 'slate', ...properties }: BadgeProperties) => (
  <span
    className={ClassNames(
      'inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs font-semibold',
      toneClasses[tone],
      className,
    )}
    {...properties}
  >
    {dot ? <span aria-hidden="true" className={ClassNames('size-1.5 shrink-0 rounded-full', dotClasses[tone])} /> : null}
    {children}
  </span>
);
